import React, { useState, useEffect } from 'react';
import { dataManagementApi } from '../services/api';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import MatchesDataTable from '../components/datamanagement/MatchesDataTable';
import MatchdaysDataTable from '../components/datamanagement/MatchdaysDataTable';
import LocalMatchdaysDataTable from '../components/datamanagement/LocalMatchdaysDataTable';
import LocalMatchesDataTable from '../components/datamanagement/LocalMatchesDataTable';
import OpenLigaDbImport from '../components/openLigaDb/OpenLigaDbImport';
import { BarChart3, LayoutGrid, List, Target, CircleDot, CalendarDays, Globe, Database, Palette, Download, Upload, FolderOpen, AlertTriangle, XCircle, RefreshCw, X } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

interface TableInfo {
  name: string;
  displayName: string;
  database: string;
  description: string;
}

interface ImportResult {
  successCount: number;
  errorCount: number;
  totalCount: number;
  errors: string[];
  warnings: string[];
  summary: string;
}

const DataManagement: React.FC = () => {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewMode, setViewMode] = usePreferredViewMode<'cards' | 'list'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMatchesTable, setShowMatchesTable] = useState(false);
  const [showMatchdaysTable, setShowMatchdaysTable] = useState(false);
  const [showLocalMatchdaysTable, setShowLocalMatchdaysTable] = useState(false);
  const [showLocalMatchesTable, setShowLocalMatchesTable] = useState(false);
  const [showOpenLigaDbImport, setShowOpenLigaDbImport] = useState(false);

  const { toasts, removeToast, showError, showSuccess } = useToast();

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      setLoading(true);
      const tables = await dataManagementApi.getTables();
      setTables(tables);
    } catch (error) {
      console.error('Error loading tables:', error);
      showError('Fehler beim Laden der Tabellen');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async (tableName: string) => {
    try {
      const blob = await dataManagementApi.downloadTemplate(tableName);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableName}_template.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccess(`Template für ${tableName} heruntergeladen`);
    } catch (error) {
      console.error('Error downloading template:', error);
      showError('Fehler beim Herunterladen des Templates');
    }
  };

  const handleExportData = async (tableName: string) => {
    try {
      const blob = await dataManagementApi.exportData(tableName);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableName}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccess(`Daten für ${tableName} exportiert`);
    } catch (error) {
      console.error('Error exporting data:', error);
      showError('Fehler beim Exportieren der Daten');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.csv')) {
        setImportFile(file);
      } else {
        showError('Bitte wählen Sie eine CSV-Datei aus');
        event.target.value = '';
      }
    }
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv')) {
        setImportFile(file);
      } else {
        showError('Bitte wählen Sie eine CSV-Datei aus');
      }
    }
  };

  const handleImport = async () => {
    if (!importFile || !selectedTable) {
      showError('Bitte wählen Sie eine Tabelle und Datei aus');
      return;
    }

    try {
      setImporting(true);
      const result = await dataManagementApi.importData(selectedTable, importFile);
      setImportResult(result);
      if (result.successCount > 0) {
        showSuccess(`${result.successCount} Datensätze erfolgreich importiert`);
      }
    } catch (error: any) {
      console.error('Error importing data:', error);
      showError('Fehler beim Importieren der Daten');
      setImportResult({
        successCount: 0,
        errorCount: 1,
        totalCount: 1,
        errors: [error.message || 'Import fehlgeschlagen'],
        warnings: [],
        summary: 'Import fehlgeschlagen'
      });
    } finally {
      setImporting(false);
    }
  };

  const openImportModal = (tableName: string) => {
    console.log('Opening import modal for:', tableName);
    setSelectedTable(tableName);
    setImportFile(null);
    setImportResult(null);
    setShowImportModal(true);
    console.log('Modal state set to true');
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setSelectedTable('');
    setImportFile(null);
    setImportResult(null);
  };

  // Filter tables based on search term
  const filteredTables = tables.filter(table =>
    table.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    table.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    table.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const coreTableCount = filteredTables.filter(table => table.database === 'Core').length;
  const customTableCount = filteredTables.filter(table => table.database === 'Custom').length;

  if (loading) {
    return (
      <div className="page-container"><div className="page-loading"><div className="page-spinner" /><p>Lade Tabellen...</p></div></div>
    );
  }

  return (
    <div className="page-container">
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button type="button" className={`page-toggle-btn${viewMode === 'cards' ? ' active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln"><LayoutGrid size={14} /></button>
            <button type="button" className={`page-toggle-btn${viewMode === 'list' ? ' active' : ''}`} onClick={() => setViewMode('list')} title="Liste"><List size={14} /></button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input className="page-search-input" type="text" placeholder="Suchen nach Tabellen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredTables.length} Tabellen · {coreTableCount} Core · {customTableCount} Custom</span>
        </div>
      </div>

      <div className="page-stats-bar">
        <div className="stat"><span className="stat-value">{filteredTables.length}</span><span className="stat-label">Gefunden</span></div>
        <div className="stat"><span className="stat-value">{coreTableCount}</span><span className="stat-label">Core DB</span></div>
        <div className="stat"><span className="stat-value">{customTableCount}</span><span className="stat-label">Custom DB</span></div>
      </div>

      {/* Erweiterte Datenansichten */}
      <h2 className="page-section-title"><Target size={16} /> Erweiterte Datenansichten</h2>
      <div className="page-advanced-grid">
        <div className="page-advanced-card">
          <div className="page-advanced-card-header">
            <h3><CircleDot size={14} /> Spiele</h3>
            <span className="page-advanced-card-badge">Flache Tabelle</span>
          </div>
          <p>Bearbeiten Sie Spiele direkt in einer flachen Tabelle mit Dropdown-Referenzen für Spieltage und Vereine.</p>
          <button type="button" className="page-advanced-card-btn" style={{ backgroundColor: showMatchesTable ? '#dc3545' : '#3b82f6' }} onClick={() => setShowMatchesTable(!showMatchesTable)}>
            {showMatchesTable ? '✕ Schließen' : 'Öffnen'}
          </button>
        </div>
        <div className="page-advanced-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
          <div className="page-advanced-card-header">
            <h3><CalendarDays size={14} /> Spieltage</h3>
            <span className="page-advanced-card-badge">Flache Tabelle</span>
          </div>
          <p>Verwalten Sie Spieltage mit allen Details in einer übersichtlichen Tabellenansicht.</p>
          <button type="button" className="page-advanced-card-btn" style={{ backgroundColor: showMatchdaysTable ? '#dc3545' : '#3b82f6' }} onClick={() => setShowMatchdaysTable(!showMatchdaysTable)}>
            {showMatchdaysTable ? '✕ Schließen' : 'Öffnen'}
          </button>
        </div>
        <div className="page-advanced-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
          <div className="page-advanced-card-header">
            <h3><CalendarDays size={14} /> Lokale Spieltage</h3>
            <span className="page-advanced-card-badge">Flache Tabelle</span>
          </div>
          <p>Organisieren Sie lokale Spieltage mit Ort, Zeit und Teilnehmerzahlen.</p>
          <button type="button" className="page-advanced-card-btn" style={{ backgroundColor: showLocalMatchdaysTable ? '#dc3545' : '#3b82f6' }} onClick={() => setShowLocalMatchdaysTable(!showLocalMatchdaysTable)}>
            {showLocalMatchdaysTable ? '✕ Schließen' : 'Öffnen'}
          </button>
        </div>
        <div className="page-advanced-card" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
          <div className="page-advanced-card-header">
            <h3><CircleDot size={14} /> Lokale Spiele</h3>
            <span className="page-advanced-card-badge">Flache Tabelle</span>
          </div>
          <p>Verwalten Sie lokale Spiele mit Teams, Ergebnissen und Spieldetails.</p>
          <button type="button" className="page-advanced-card-btn" style={{ backgroundColor: showLocalMatchesTable ? '#dc3545' : '#3b82f6' }} onClick={() => setShowLocalMatchesTable(!showLocalMatchesTable)}>
            {showLocalMatchesTable ? '✕ Schließen' : 'Öffnen'}
          </button>
        </div>
        <div className="page-advanced-card" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
          <div className="page-advanced-card-header">
            <h3><Globe size={14} /> OpenLigaDB</h3>
            <span className="page-advanced-card-badge">API Import</span>
          </div>
          <p>Importieren Sie Bundesliga-Spiele und Teams direkt von OpenLigaDB für Ihren Verein.</p>
          <button type="button" className="page-advanced-card-btn" style={{ backgroundColor: showOpenLigaDbImport ? '#dc3545' : '#3b82f6' }} onClick={() => setShowOpenLigaDbImport(!showOpenLigaDbImport)}>
            {showOpenLigaDbImport ? '✕ Schließen' : 'Öffnen'}
          </button>
        </div>
      </div>

      {showMatchesTable && <div className="page-data-section"><MatchesDataTable /></div>}
      {showMatchdaysTable && <div className="page-data-section"><MatchdaysDataTable /></div>}
      {showLocalMatchdaysTable && <div className="page-data-section"><LocalMatchdaysDataTable /></div>}
      {showLocalMatchesTable && <div className="page-data-section"><LocalMatchesDataTable /></div>}
      {showOpenLigaDbImport && <div className="page-data-section"><OpenLigaDbImport /></div>}

      {/* Content */}
      {viewMode === 'cards' ? (
        <div>
          <div style={{ marginBottom: '2rem' }}>
            <h2 className="page-section-title"><Database size={16} /> Core Database</h2>
            <div className="page-cards-grid">
              {filteredTables.filter(table => table.database === 'Core').map((table) => (
                <div key={table.name} className="page-card">
                  <div className="page-card-header"><h3 className="page-card-title">{table.displayName}</h3><span className="page-badge page-badge-info">Core</span></div>
                  <p className="page-card-description">{table.description}</p>
                  <div className="page-card-actions">
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#3b82f6' }} onClick={() => handleDownloadTemplate(table.name)} title="Template herunterladen"><Download size={12} /> Template</button>
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#10b981' }} onClick={() => handleExportData(table.name)} title="Daten exportieren"><Upload size={12} /> Export</button>
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#f59e0b' }} onClick={() => openImportModal(table.name)} title="Daten importieren"><FolderOpen size={12} /> Import</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h2 className="page-section-title"><Palette size={16} /> Custom Database</h2>
            <div className="page-cards-grid">
              {filteredTables.filter(table => table.database === 'Custom').map((table) => (
                <div key={table.name} className="page-card">
                  <div className="page-card-header"><h3 className="page-card-title">{table.displayName}</h3><span className="page-badge" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>Custom</span></div>
                  <p className="page-card-description">{table.description}</p>
                  <div className="page-card-actions">
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#3b82f6' }} onClick={() => handleDownloadTemplate(table.name)} title="Template herunterladen"><Download size={12} /> Template</button>
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#10b981' }} onClick={() => handleExportData(table.name)} title="Daten exportieren"><Upload size={12} /> Export</button>
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#f59e0b' }} onClick={() => openImportModal(table.name)} title="Daten importieren"><FolderOpen size={12} /> Import</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <table className="page-table">
          <thead>
            <tr>
              <th>Tabelle</th>
              <th>Datenbank</th>
              <th>Beschreibung</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredTables.map((table) => (
              <tr key={table.name}>
                <td><strong>{table.displayName}</strong><br /><span className="page-table-meta">{table.name}</span></td>
                <td>
                  <span className="page-badge" style={{ backgroundColor: table.database === 'Core' ? '#dbeafe' : '#ede9fe', color: table.database === 'Core' ? '#1e40af' : '#7c3aed' }}>
                    {table.database}
                  </span>
                </td>
                <td className="page-table-meta">{table.description}</td>
                <td>
                  <div className="page-table-actions">
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#3b82f6', flex: 'none', minWidth: 'auto', padding: '0.5rem' }} onClick={() => handleDownloadTemplate(table.name)} title="Template herunterladen"><Download size={12} /></button>
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#10b981', flex: 'none', minWidth: 'auto', padding: '0.5rem' }} onClick={() => handleExportData(table.name)} title="Daten exportieren"><Upload size={12} /></button>
                    <button type="button" className="page-action-btn" style={{ backgroundColor: '#f59e0b', flex: 'none', minWidth: 'auto', padding: '0.5rem' }} onClick={() => openImportModal(table.name)} title="Daten importieren"><FolderOpen size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="page-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeImportModal(); }}>
          <div className="page-modal-content" style={{ width: '500px', maxWidth: '90vw', padding: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0 }}><FolderOpen size={16} /> Daten importieren - {tables.find(t => t.name === selectedTable)?.displayName}</h3>
              <button type="button" className="page-toggle-btn" onClick={closeImportModal}>×</button>
            </div>

            <div style={{ padding: '1.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="page-form-field">
                <label>CSV-Datei zum Importieren auswählen</label>
                <input id="file-input" type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
                <div className="page-file-drop" onDragOver={handleDragOver} onDrop={handleDrop} onClick={triggerFileInput}>
                  {importFile ? (
                    <>
                      <div style={{ fontSize: '2.5rem', color: '#10b981' }}>✓</div>
                      <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#059669' }}>{importFile.name}</p>
                      <p className="page-table-meta">Klicken Sie, um eine andere Datei auszuwählen</p>
                    </>
                  ) : (
                    <>
                      <FolderOpen size={24} color="#9ca3af" />
                      <p style={{ fontSize: '0.875rem', fontWeight: 500 }}>Klicken Sie, um eine CSV-Datei auszuwählen</p>
                      <p className="page-table-meta">oder ziehen Sie eine Datei hierher</p>
                    </>
                  )}
                </div>
                <p className="page-table-meta" style={{ textAlign: 'center', marginTop: '0.5rem' }}>Nur CSV-Dateien werden unterstützt</p>
              </div>

              {importResult && (
                <div className="page-import-result">
                  <h4><BarChart3 size={14} /> Import-Ergebnis</h4>
                  <div style={{ background: '#f9fafb', padding: '0.75rem', borderRadius: '6px', marginBottom: '0.75rem' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>{importResult.summary}</p>
                  </div>
                  {importResult.warnings.length > 0 && (
                    <div className="page-import-warnings">
                      <p><AlertTriangle size={14} /> Warnungen:</p>
                      <ul className="page-import-list">{importResult.warnings.map((w, i) => <li key={i}><span>•</span><span>{w}</span></li>)}</ul>
                    </div>
                  )}
                  {importResult.errors.length > 0 && (
                    <div className="page-import-errors">
                      <p><XCircle size={14} /> Fehler:</p>
                      <ul className="page-import-list">{importResult.errors.map((e, i) => <li key={i}><span>•</span><span>{e}</span></li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="page-modal-actions" style={{ padding: '1.5rem', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button type="button" className="page-toggle-btn page-btn-create" onClick={handleImport} disabled={!importFile || importing} style={{ flex: 1, opacity: !importFile || importing ? 0.6 : 1 }}>
                {importing ? <><RefreshCw size={14} className="page-spinner" /> Importiere...</> : 'Import starten'}
              </button>
              <button type="button" className="page-toggle-btn" onClick={closeImportModal} style={{ flex: 1 }}>
                <X size={14} /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagement;
